import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import SkipToContent from './components/SkipToContent';
import HeroSection from './components/HeroSection';
import AISection from './components/AISection';
import AgencySection from './components/AgencySection';
import CaseStudiesSection from './components/CaseStudiesSection';
import TestimonialsSection from './components/TestimonialsSection';
import PricingSection from './components/PricingSection';
import BlogSection from './components/BlogSection';
import BlogPost from './components/BlogPost';
import Footer from './components/Footer';
import { getCurrentUser } from './lib/auth';
import { User, Tab } from './lib/types';
import Dashboard from './components/Dashboard';
import PimperPage from './components/PimperPage';
import AdminUsers from './pages/AdminUsers';
import AdminCatalogue from './pages/AdminCatalogue';
import ResourcePage from './components/ResourcePage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import UserProfile from './components/UserProfile';
import JobPostGenerator from './components/JobPostGenerator';
import ResourcesPanel from './components/ResourcesPanel';
import SettingsPanel from './components/SettingsPanel';

function App() {
  const [user, setUser] = useState<User | null>(() => {
    console.log('Initializing user state from localStorage');
    return getCurrentUser();
  });
  const [activeTab, setActiveTab] = useState<Tab>('account');

  useEffect(() => {
    console.log('User state changed:', user);
  }, [user]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'current_user') {
        console.log('LocalStorage user changed, updating state');
        setUser(getCurrentUser());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLoginSuccess = (loggedInUser: User) => {
    console.log('Login success handler called with user:', loggedInUser);
    setUser(loggedInUser);
    setActiveTab('account');
    console.log('User state updated after login');
  };

  const handleLogout = () => {
    console.log('Logging out...');
    setUser(null);
    console.log('User state cleared');
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
  };

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    console.log('Protected route check - Current user:', user);
    if (!user) {
      console.log('No user found, redirecting to home');
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  };

  const AdminRoute = ({ children }: { children: React.ReactNode }) => {
    console.log('Admin route check - Current user:', user);
    if (!user || user.role !== 'admin') {
      console.log('Not admin, redirecting to home');
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  };

  const LandingPage = () => (
    <>
      <HeroSection />
      <AISection />
      <AgencySection />
      <CaseStudiesSection />
      <TestimonialsSection />
      <PricingSection />
      <BlogSection />
      <Footer />
    </>
  );

  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <Helmet defaultTitle="PimpMyJob - Générateur d'offres d'emploi IA">
          <html lang="fr" />
          <meta name="description" content="Créez des offres d'emploi optimisées avec l'IA. Attirez les meilleurs talents avec des annonces professionnelles générées en quelques clics." />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        </Helmet>
        
        <SkipToContent />
        <Navbar user={user} onLoginSuccess={handleLoginSuccess} onLogout={handleLogout} />
        
        <main id="main-content" className="flex-1 pt-16">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/resources/:id" element={<ResourcePage />} />
            
            {/* Protected routes with shared layout */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab={activeTab} onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <Dashboard 
                      user={user!} 
                      onLogout={handleLogout} 
                      activeTab={activeTab} 
                      onTabChange={handleTabChange} 
                    />
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/account" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="account" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <UserProfile user={user!} onLogout={handleLogout} />
                  </div>
                </div>
              </ProtectedRoute>
            } />

            <Route path="/settings" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="setup" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <SettingsPanel user={user!} />
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/pimper" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="pimper" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <PimperPage 
                      user={user!} 
                      activeTab="pimper" 
                      onTabChange={handleTabChange} 
                    />
                  </div>
                </div>
              </ProtectedRoute>
            } />

            <Route path="/générer" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="write" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <JobPostGenerator user={user!} />
                  </div>
                </div>
              </ProtectedRoute>
            } />

            <Route path="/resources" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="learn" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <ResourcesPanel />
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/subscription" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="bank" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <PricingSection />
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/subscription/success" element={
              <ProtectedRoute>
                <SubscriptionSuccessPage />
              </ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin/users" element={
              <AdminRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="users" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <AdminUsers />
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/admin/catalogue" element={
              <AdminRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="catalogue" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <AdminCatalogue />
                  </div>
                </div>
              </AdminRoute>
            } />

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;